import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  addVeterinarian,
  updateVeterinarian,
  deleteVeterinarian,
} from '../../store/settingsSlice'
import type { Veterinarian } from '../../store/settingsSlice'
import { Pencil, Trash2, Plus, X, Check } from 'lucide-react'

interface VetFormData {
  name: string
  license_number: string
  specialties: string
}

const emptyForm: VetFormData = { name: '', license_number: '', specialties: '' }

export function VeterinariansSection() {
  const dispatch = useAppDispatch()
  const veterinarians = useAppSelector((s) => s.settings.veterinarians)
  const vetsLoading = useAppSelector((s) => s.settings.vetsLoading)

  const [adding, setAdding] = useState(false)
  const [addForm, setAddForm] = useState<VetFormData>(emptyForm)
  const [addErrors, setAddErrors] = useState<string[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<VetFormData>(emptyForm)
  const [editErrors, setEditErrors] = useState<string[]>([])
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const validateForm = (form: VetFormData): string[] => {
    const errors: string[] = []
    if (!form.name.trim()) errors.push('Name is required')
    if (!form.license_number.trim()) errors.push('License number is required')
    return errors
  }

  const handleAdd = async () => {
    const errors = validateForm(addForm)
    if (errors.length > 0) {
      setAddErrors(errors)
      return
    }
    setAddErrors([])
    await dispatch(addVeterinarian(addForm)).unwrap()
    setAddForm(emptyForm)
    setAdding(false)
  }

  const startEdit = (vet: Veterinarian) => {
    setEditingId(vet.id)
    setEditForm({
      name: vet.name,
      license_number: vet.license_number || '',
      specialties: vet.specialties || '',
    })
    setEditErrors([])
  }

  const handleUpdate = async () => {
    if (!editingId) return
    const errors = validateForm(editForm)
    if (errors.length > 0) {
      setEditErrors(errors)
      return
    }
    setEditErrors([])
    await dispatch(updateVeterinarian({ id: editingId, ...editForm })).unwrap()
    setEditingId(null)
  }

  const handleDelete = async (id: string) => {
    await dispatch(deleteVeterinarian(id)).unwrap()
    setConfirmDeleteId(null)
  }

  if (vetsLoading) {
    return (
      <section data-testid="veterinarians-section">
        <h2 className="text-[14px] font-medium text-text-primary mb-4">Veterinarians</h2>
        <p className="text-[12px] text-text-muted">Loading...</p>
      </section>
    )
  }

  return (
    <section data-testid="veterinarians-section" className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[14px] font-medium text-text-primary">Veterinarians</h2>
        {!adding && (
          <button
            data-testid="add-veterinarian-button"
            onClick={() => { setAdding(true); setAddForm(emptyForm); setAddErrors([]) }}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            <Plus size={14} />
            <span>Add Veterinarian</span>
          </button>
        )}
      </div>

      <div className="flex flex-col gap-1">
        {veterinarians.map((vet) => (
          <div key={vet.id}>
            {editingId === vet.id ? (
              <div data-testid={`vet-edit-form-${vet.id}`} className="flex flex-col gap-2 p-3 bg-surface rounded-[6px] border border-border">
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-text-muted">Name</label>
                  <input
                    data-testid="vet-edit-name"
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-text-muted">License Number</label>
                  <input
                    data-testid="vet-edit-license"
                    type="text"
                    value={editForm.license_number}
                    onChange={(e) => setEditForm({ ...editForm, license_number: e.target.value })}
                    className="h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-text-muted">Specialties</label>
                  <input
                    data-testid="vet-edit-specialties"
                    type="text"
                    value={editForm.specialties}
                    onChange={(e) => setEditForm({ ...editForm, specialties: e.target.value })}
                    className="h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
                  />
                </div>
                {editErrors.length > 0 && (
                  <div data-testid="vet-edit-errors" className="text-[11px] text-status-error">
                    {editErrors.map((err, i) => <p key={i}>{err}</p>)}
                  </div>
                )}
                <div className="flex gap-1">
                  <button
                    data-testid="vet-edit-save"
                    onClick={handleUpdate}
                    className="inline-flex items-center gap-1 h-6 px-2 rounded-[4px] bg-accent text-white text-[12px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
                  >
                    <Check size={12} />
                    Save
                  </button>
                  <button
                    data-testid="vet-edit-cancel"
                    onClick={() => setEditingId(null)}
                    className="inline-flex items-center gap-1 h-6 px-2 rounded-[4px] text-[12px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
                  >
                    <X size={12} />
                    Cancel
                  </button>
                </div>
              </div>
            ) : confirmDeleteId === vet.id ? (
              <div data-testid={`vet-delete-confirm-${vet.id}`} className="flex items-center justify-between p-2 bg-surface rounded-[6px] border border-status-error/30">
                <span className="text-[12px] text-text-primary">Remove {vet.name}?</span>
                <div className="flex gap-1">
                  <button
                    data-testid="vet-delete-confirm-yes"
                    onClick={() => handleDelete(vet.id)}
                    className="h-6 px-2 rounded-[4px] bg-status-error text-white text-[12px] hover:opacity-80 transition-colors duration-100 cursor-pointer"
                  >
                    Remove
                  </button>
                  <button
                    data-testid="vet-delete-confirm-cancel"
                    onClick={() => setConfirmDeleteId(null)}
                    className="h-6 px-2 rounded-[4px] text-[12px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                data-testid={`vet-row-${vet.id}`}
                className="flex items-center justify-between h-[44px] px-2 rounded-[4px] hover:bg-hover transition-colors duration-100"
              >
                <div className="flex flex-col">
                  <span data-testid={`vet-name-${vet.id}`} className="text-[13px] font-medium text-text-primary">
                    {vet.name}
                  </span>
                  <span className="text-[11px] text-text-muted">
                    {vet.license_number && <span data-testid={`vet-license-${vet.id}`}>License: {vet.license_number}</span>}
                    {vet.license_number && vet.specialties && ' · '}
                    {vet.specialties && <span data-testid={`vet-specialties-${vet.id}`}>{vet.specialties}</span>}
                  </span>
                </div>
                <div className="flex gap-0.5">
                  <button
                    data-testid={`vet-edit-button-${vet.id}`}
                    onClick={() => startEdit(vet)}
                    className="flex items-center justify-center w-7 h-7 rounded-[4px] text-text-muted hover:bg-hover hover:text-text-primary transition-colors duration-100 cursor-pointer"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    data-testid={`vet-delete-button-${vet.id}`}
                    onClick={() => setConfirmDeleteId(vet.id)}
                    className="flex items-center justify-center w-7 h-7 rounded-[4px] text-text-muted hover:bg-hover hover:text-status-error transition-colors duration-100 cursor-pointer"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {veterinarians.length === 0 && !adding && (
          <p className="text-[12px] text-text-muted py-2">No veterinarians configured yet.</p>
        )}
      </div>

      {adding && (
        <div data-testid="vet-add-form" className="flex flex-col gap-2 p-3 bg-surface rounded-[6px] border border-border">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Name</label>
            <input
              data-testid="vet-add-name"
              type="text"
              value={addForm.name}
              onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
              placeholder="e.g., Dr. Jane Smith"
              className="h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">License Number</label>
            <input
              data-testid="vet-add-license"
              type="text"
              value={addForm.license_number}
              onChange={(e) => setAddForm({ ...addForm, license_number: e.target.value })}
              placeholder="e.g., DVM-001"
              className="h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Specialties</label>
            <input
              data-testid="vet-add-specialties"
              type="text"
              value={addForm.specialties}
              onChange={(e) => setAddForm({ ...addForm, specialties: e.target.value })}
              placeholder="e.g., Surgery, Dentistry"
              className="h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>
          {addErrors.length > 0 && (
            <div data-testid="vet-add-errors" className="text-[11px] text-status-error">
              {addErrors.map((err, i) => <p key={i}>{err}</p>)}
            </div>
          )}
          <div className="flex gap-1">
            <button
              data-testid="vet-add-save"
              onClick={handleAdd}
              className="inline-flex items-center gap-1 h-6 px-2 rounded-[4px] bg-accent text-white text-[12px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
            >
              <Check size={12} />
              Save
            </button>
            <button
              data-testid="vet-add-cancel"
              onClick={() => { setAdding(false); setAddErrors([]) }}
              className="inline-flex items-center gap-1 h-6 px-2 rounded-[4px] text-[12px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
            >
              <X size={12} />
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
